import axiosInstance from "../../utils/axiosInstance";
import { API_URL } from "../../utils/runtimeConfig";

const apiUrl = API_URL;

const buildQueryString = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const normalized = String(value).trim();
    if (!normalized) return;
    query.set(key, normalized);
  });
  return query.toString();
};

export const listCommanderProfiles = async ({ q = "", limit = 120 } = {}) => {
  const query = buildQueryString({ q, limit });
  const suffix = query ? `?${query}` : "";
  const response = await axiosInstance.get(`${apiUrl}/battle-simulation/profiles${suffix}`);
  return Array.isArray(response?.data?.profiles) ? response.data.profiles : [];
};

export const listCombatProfiles = async ({
  q = "",
  nationId = "",
  nationNumber = "",
  allianceShort = "",
  gameCommanderId = "",
  limit = 120,
} = {}) => {
  const query = buildQueryString({
    q,
    nationId,
    nationNumber,
    allianceShort,
    gameCommanderId,
    limit,
  });
  const suffix = query ? `?${query}` : "";
  const response = await axiosInstance.get(`${apiUrl}/battle-simulation/combat-profiles${suffix}`);
  return Array.isArray(response?.data?.profiles)
    ? response.data.profiles
    : (Array.isArray(response?.data?.combatProfiles) ? response.data.combatProfiles : []);
};

export const createCommanderProfile = async (payload = {}) => {
  const response = await axiosInstance.post(`${apiUrl}/battle-simulation/profiles`, payload);
  return response?.data?.profile || null;
};

export const getCommanderProfile = async (profileId) => {
  const response = await axiosInstance.get(`${apiUrl}/battle-simulation/profiles/${profileId}`);
  return response?.data?.profile || null;
};

export const updateCommanderProfile = async (profileId, payload = {}) => {
  const response = await axiosInstance.patch(`${apiUrl}/battle-simulation/profiles/${profileId}`, payload);
  return response?.data?.profile || null;
};

export const deleteCommanderProfile = async (profileId) => {
  const response = await axiosInstance.delete(`${apiUrl}/battle-simulation/profiles/${profileId}`);
  return Boolean(response?.data?.deleted);
};

export const getMyCommanderProfile = async () => {
  const response = await axiosInstance.get(`${apiUrl}/my-commander/profile`);
  return response?.data?.profile || null;
};

export const updateMyCommanderProfile = async (payload = {}) => {
  const response = await axiosInstance.patch(`${apiUrl}/my-commander/profile`, payload);
  return response?.data?.profile || null;
};

export const listMyCommanderStatOptions = async () => {
  const response = await axiosInstance.get(`${apiUrl}/my-commander/stat-options`);
  return response?.data || { statOptions: [], catalog: null };
};

export const getMyCommanderRecommendations = async (payload = {}) => {
  const response = await axiosInstance.post(`${apiUrl}/my-commander/recommendations`, payload);
  return response?.data || null;
};

export const getMyCommanderLinkCandidate = async (commanderId) => {
  const query = buildQueryString({ commanderId });
  if (!query) return null;
  const response = await axiosInstance.get(`${apiUrl}/my-commander/link-candidate?${query}`);
  return response?.data?.commander || null;
};

export const linkMyCommanderByCommanderId = async (commanderId) => {
  const response = await axiosInstance.post(`${apiUrl}/my-commander/link-commander`, { commanderId });
  return response?.data || null;
};

export const listBattleSeeds = async ({
  linkedCommanderId = "",
  reportId = "",
  nationId = "",
  action = "",
  limit = 120,
} = {}) => {
  const query = buildQueryString({
    linkedCommanderId,
    reportId,
    nationId,
    action,
    limit,
  });
  const suffix = query ? `?${query}` : "";
  const response = await axiosInstance.get(`${apiUrl}/battle-simulation/seeds${suffix}`);
  return Array.isArray(response?.data?.seeds) ? response.data.seeds : [];
};

export const getBattleSeed = async (seedId) => {
  const response = await axiosInstance.get(`${apiUrl}/battle-simulation/seeds/${seedId}`);
  return response?.data?.seed || null;
};

export const createBattleSeed = async (payload = {}) => {
  const response = await axiosInstance.post(`${apiUrl}/battle-simulation/seeds`, payload);
  return response?.data?.seed || null;
};

export const updateBattleSeedMetadata = async (seedId, payload = {}) => {
  const response = await axiosInstance.patch(`${apiUrl}/battle-simulation/seeds/${seedId}`, payload);
  return response?.data?.seed || null;
};

export const listReportBaselines = async ({
  reportId = "",
  action = "",
  scope = "",
  nationId = "",
  limit = 120,
} = {}) => {
  const query = buildQueryString({
    reportId,
    action,
    scope,
    nationId,
    limit,
  });
  const suffix = query ? `?${query}` : "";
  try {
    const response = await axiosInstance.get(`${apiUrl}/battle-reports${suffix}`);
    return Array.isArray(response?.data?.battleReports)
      ? response.data.battleReports
      : (Array.isArray(response?.data?.reportBaselines) ? response.data.reportBaselines : []);
  } catch (error) {
    const response = await axiosInstance.get(`${apiUrl}/battle-simulation/report-baselines${suffix}`);
    return Array.isArray(response?.data?.reportBaselines) ? response.data.reportBaselines : [];
  }
};

export const createReportBaseline = async (payload = {}) => {
  try {
    const response = await axiosInstance.post(`${apiUrl}/battle-reports/import`, payload);
    return response?.data?.battleReport || response?.data?.reportBaseline || null;
  } catch (error) {
    const response = await axiosInstance.post(`${apiUrl}/battle-simulation/report-baselines`, payload);
    return response?.data?.reportBaseline || null;
  }
};

export const getReportBaseline = async (reportBaselineId, { scope = "", nationId = "" } = {}) => {
  const query = buildQueryString({ scope, nationId });
  const suffix = query ? `?${query}` : "";
  try {
    const response = await axiosInstance.get(`${apiUrl}/battle-simulation/report-baselines/${reportBaselineId}${suffix}`);
    return response?.data?.reportBaseline || response?.data?.battleReport || null;
  } catch (error) {
    const response = await axiosInstance.get(`${apiUrl}/battle-reports/${reportBaselineId}${suffix}`);
    return response?.data?.battleReport || response?.data?.reportBaseline || null;
  }
};

export const runBattleParityCompare = async (payload = {}) => {
  const response = await axiosInstance.post(`${apiUrl}/battle-simulation/parity/compare`, payload);
  return response?.data || null;
};

export const searchCommandersByName = async (name, { page = 1, limit = 10 } = {}) => {
  const query = buildQueryString({ name, page, limit });
  if (!query) return [];
  const response = await axiosInstance.get(`${apiUrl}/commanders/search?${query}`);
  return Array.isArray(response?.data?.commanders) ? response.data.commanders : [];
};

export const getCommanderById = async (commanderId) => {
  const id = String(commanderId || "").trim();
  if (!id) return null;
  const response = await axiosInstance.get(`${apiUrl}/commanders/${id}`);
  return response?.data || null;
};
